import type { IntuneOrphan } from '@/types/oscar';

/**
 * What a helpdesk person triages an Intune orphan on (Anthony, 01/11/2026: "it needs to bring more
 * information in, currently it's only showing an ID").
 *
 * ⚠ A device that has not checked in for this long is probably gone (re-imaged, lost, retired in Intune
 * but never removed), not "missing from Oscar". The page flags it so the first question is answered at a glance.
 */
export const STALE_CHECK_IN_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days since a timestamp, or null when there is none (the sync has not filled it in yet). */
export function daysSince(value: string | null | undefined, now: Date = new Date()): number | null {
  if (!value) return null;
  const t = Date.parse(value);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now.getTime() - t) / DAY_MS));
}

/**
 * ⛔ NULL IS NOT STALE. A device whose check-in is unknown (the liveness columns arrive with the next sync
 * after the migration) must not be flagged as dead; it is simply not known yet.
 */
export function isStaleCheckIn(value: string | null | undefined, now: Date = new Date()): boolean {
  const days = daysSince(value, now);
  return days !== null && days >= STALE_CHECK_IN_DAYS;
}

/** The line that names the device: its hostname, else what we do know, else that it has no name. */
export function deviceTitle(o: Pick<IntuneOrphan, 'Hostname' | 'Manufacturer' | 'Model' | 'SerialNumber'>): string {
  const host = o.Hostname?.trim();
  if (host) return host;
  const make = [o.Manufacturer, o.Model].map((v) => v?.trim()).filter(Boolean).join(' ');
  if (make) return make;
  return o.SerialNumber?.trim() || 'Unnamed device';
}

/** Make and model on one line, or null when Intune told us neither. */
export function makeModel(o: Pick<IntuneOrphan, 'Manufacturer' | 'Model'>): string | null {
  const text = [o.Manufacturer, o.Model].map((v) => v?.trim()).filter(Boolean).join(' ');
  return text || null;
}

/** Everything the search box matches on, so "kim", an IMEI, a model or a hostname all find the row. */
export function searchText(o: IntuneOrphan): string[] {
  return [
    o.SerialNumber, o.OsType, o.OsVersion, o.AssignedUpn, o.AssignedPersonName, o.Hostname,
    o.Manufacturer, o.Model, o.Imei, o.PhoneNumber, o.LastLogonUpn, o.IntuneDeviceId,
  ].map((v) => (v ?? '').toLowerCase());
}
